<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <form-card
            :title="$t(tKey + '.general.title')"
            :description="$t(tKey + '.general.description')"
          >
            <text-input
              v-model="rawSection.slug"
              type="text"
              name="slug"
              class="my-4"
              :error="errors?.slug"
              :label="$t('faqSection.slug')"
            />
            <div class="grid grid-cols-2 gap-x-5">
              <Switch
                v-model="rawSection.showInApp"
                :label="$t('faqSection.showInApp')"
                class="mt-4 mb-10"
              />
              <Switch
                v-model="rawSection.showInForBusinessApp"
                :label="$t('faqSection.showInForBusinessApp')"
                class="mt-4 mb-10"
              />
            </div>
            <translation-accordion v-slot="{ translation }" v-model="rawSection">
              <text-input
                v-model="(translation as FaqSectionTranslation).title"
                type="text"
                name="title"
                :error="errors?.title"
                :label="$t('faqSection.title')"
              />
            </translation-accordion>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  array, boolean, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import TextInput from '@utils/inputs/Input.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { syncRef } from '@hooks/state';
import TranslationAccordion from '@utils/language/TranslationAccordion.vue';
import Switch from '@utils/inputs/Switch.vue';
import type {
  FaqSection, FaqSectionTranslation,
} from '@/entities/Entities';

interface Props {
  open: boolean;
  section?: FaqSection;
}

const emit = defineEmits(['update:open', 'update:section', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalSection = useVModel(props, 'section', emit);

const isNew = computed(() => !props.section);
const tKey = computed(() => (isNew.value ? 'pages.faqSectionList.modal.new' : 'pages.faqSectionList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

interface RawTranslation {
  '@id'?: string;
  id?: string;
  title?: string;
  language: string;
}

const getMainTranslation = (translations: RawTranslation[]) => translations?.find((translation) => translation?.language === 'de');

const getRawTranslations = (translations: RawTranslation[]): RawTranslation[] => translations.map((translation) => ({
  id: translation['@id'],
  title: translation.title,
  language: translation.language,
}));

const initialData = (section?: FaqSection): RawTranslation[] => (section
  ? getRawTranslations(
    section.translations,
  )
  : [
  ]);

const rawSection = syncRef(internalSection, (section) => ({
  slug: section?.slug,
  translations: initialData(section),
  showInApp: section?.showInApp ?? false,
  showInForBusinessApp: section?.showInForBusinessApp ?? false,
}));

const {
  data: section,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    slug: string().nullable().required(t('validation.notEmpty')),
    translations: array(
      object({
        title: string().nullable(),
      }),
    ),
    showInApp: boolean().required(t('validation.notEmpty')),
    showInForBusinessApp: boolean().required(t('validation.notEmpty')),
  }),
  rawSection,
);

function resetData() {
  rawSection.value = { ...initialData() } as unknown as typeof section['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalSection.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.section;
    const url = edit ? props.section['@id'] : '/faq_sections';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        translations: section.value.translations.filter((trans) => trans),
        slug: section.value.slug,
        showInApp: section.value.showInApp,
        showInForBusinessApp: section.value.showInForBusinessApp,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>

